<template>
  <v-main>
    <v-container fluid>
      <!-- CertifAI Platform Guide - Hero Section -->
      <v-card class="mb-6 hero-guide-card" elevation="4">
        <!-- Gradient Header -->
        <div class="hero-header">
          <div class="hero-header-content">
            <div class="d-flex align-center mb-2">
              <v-avatar size="48" class="mr-3 hero-logo">
                <v-icon size="28" color="white">mdi-shield-check-outline</v-icon>
              </v-avatar>
              <div>
                <h1 class="hero-title">Welcome to CertifAI</h1>
                <p class="hero-subtitle">Your Certification Journey Starts Here</p>
              </div>
            </div>
          </div>
          <!-- Social Media Links -->
          <div class="social-links">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="social-btn"
                  href="https://certifai.info/"
                  target="_blank"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon color="#E3F1FF" size="20">mdi-web</v-icon>
                </v-btn>
              </template>
              <span>Visit Website</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="social-btn"
                  href="https://twitter.com/CertifA20221"
                  target="_blank"
                  v-bind="attrs"
                  v-on="on"
                >
                  <img src="/x-logo-ts.svg" alt="X" width="16" height="16" color="whire" />
                </v-btn>
              </template>
              <span>Follow on X</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="social-btn"
                  href="https://www.youtube.com/@certifai-dt4zy"
                  target="_blank"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon color="red" size="20">mdi-youtube</v-icon>
                </v-btn>
              </template>
              <span>Watch on YouTube</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="social-btn"
                  href="http://linkedin.com/showcase/certifaieu"
                  target="_blank"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon color="blue" size="20">mdi-linkedin</v-icon>
                </v-btn>
              </template>
              <span>Connect on LinkedIn</span>
            </v-tooltip>
          </div>
        </div>

        <v-card-text class="pt-6">
          <!-- Platform Description -->
          <p class="text-body-1 mb-5 intro-text">
            CertifAI is an integrated framework designed to streamline the certification process for
            devices and systems under European regulations. Achieve compliance with
            <strong>IEC 62443</strong>, <strong>Cyber Resilience Act (CRA)</strong>, and more.
          </p>

          <!-- Help & Learning Card -->
          <v-card class="mb-6 helper-card" elevation="2">
            <v-card-text>
              <v-row no-gutters>
                <v-col cols="12" md="8">
                  <h3 class="text-h6 font-weight-bold mb-2 text-primary-600">
                    <v-icon class="mr-2">mdi-book-open-page-variant</v-icon>
                    Need Help? Learn CertifAI Concepts
                  </h3>
                  <p class="text-body-2 mb-3">
                    Explore our comprehensive user guide to understand Security Assurance Cases,
                    Claims & Evidence, Threat Modeling, Standards like CRA and IEC 62443, and much
                    more.
                  </p>
                  <p class="text-caption text-secondary">
                    <strong>Topics covered:</strong> Platform overview • Key concepts • Standards &
                    compliance • Tool explanations • Step-by-step workflows
                  </p>
                </v-col>
                <v-col cols="12" md="4" class="d-flex align-center justify-md-end justify-center">
                  <div class="d-flex flex-column align-center" style="gap:12px">
                    <v-btn
                      color="blue"
                      size="large"
                      class="white--text"
                      @click="$router.push({ name: 'Helper' })"
                    >
                      <v-icon left>mdi-help-circle</v-icon>
                      Open Help Center
                    </v-btn>
                    <v-btn
                      v-if="$store.getters.ragAnalyzerEnabled"
                      color="deep-purple"
                      size="large"
                      class="white--text"
                      @click="$router.push({ name: 'RagAnalyzer' })"
                    >
                      <v-icon left>mdi-robot-outline</v-icon>
                      AI Standards Assistant
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Getting Started Workflow -->
          <div class="workflow-section mb-6">
            <div class="section-header mb-4">
              <v-icon color="primary" size="28" class="mr-2">mdi-rocket-launch</v-icon>
              <span class="text-h6 font-weight-bold"
                >Getting Started - Your Certification Workflow</span
              >
            </div>

            <v-row class="workflow-steps">
              <!-- Step 1: Threat Spider -->
              <v-col cols="12" md="3" class="workflow-col">
                <div class="workflow-step step-1">
                  <div class="step-number recommended">1</div>
                  <div class="step-icon-wrapper recommended-icon">
                    <v-icon size="40" color="white">mdi-spider</v-icon>
                  </div>
                  <div class="recommended-badge">
                    <v-icon x-small color="white">mdi-star</v-icon>
                    Recommended First
                  </div>
                  <h3 class="step-title">Threat Modeling</h3>
                  <p class="step-description">
                    Start with <strong>threat modeling</strong> and
                    <strong>risk assessment</strong> using our powerful Threat Spider tool to
                    identify and analyze potential security threats.
                  </p>
                  <v-btn
                    color="deep-orange"
                    class="step-btn mt-3"
                    @click="$router.push({ name: 'ThreatSpiderProjects' })"
                  >
                    <v-icon left small>mdi-spider-web</v-icon>
                    Open Threat Spider
                  </v-btn>
                </div>
              </v-col>

              <!-- Step 2: Create SoC -->
              <v-col cols="12" md="3" class="workflow-col">
                <div class="workflow-step step-2">
                  <div class="step-number">2</div>
                  <div class="step-icon-wrapper">
                    <v-icon size="40" color="white">mdi-checkbox-marked-outline</v-icon>
                  </div>
                  <h3 class="step-title">Define Subject of Certification</h3>
                  <p class="step-description">
                    Define your <strong>Subject of Certification (SoC),</strong> - the system or
                    device you want to certify. This is the foundation of your compliance journey.
                  </p>
                  <v-btn
                    color="blue"
                    class="step-btn mt-3"
                    @click="$router.push({ name: 'SubjectOfCertification' })"
                  >
                    <v-icon left small>mdi-plus</v-icon>
                    Define Subjects of Certification
                  </v-btn>
                </div>
              </v-col>

              <!-- Step 3: Create SAC -->
              <v-col cols="12" md="3" class="workflow-col">
                <div class="workflow-step step-3">
                  <div class="step-number-3">3</div>
                  <div class="step-icon-wrapper secondary-icon">
                    <v-icon size="40" color="white">mdi mdi-security</v-icon>
                  </div>
                  <h3 class="step-title">Build Security Assurance Cases</h3>
                  <p class="step-description">
                    Create your <strong>Security Assurance Case (SAC),</strong> to document security
                    claims, evidence, and arguments that demonstrate your system meets compliance
                    requirements.
                  </p>
                  <v-btn
                    color="purple"
                    class="step-btn mt-3"
                    @click="$router.push({ name: 'SacBuilder' })"
                  >
                    <v-icon left small>mdi-plus</v-icon>
                    Create Security Assurance Cases
                  </v-btn>
                </div>
              </v-col>
              <!-- Step 4: Continious assessment -->
              <v-col cols="12" md="3" class="workflow-col">
                <div class="workflow-step step-4">
                  <div class="step-number-4">4</div>
                  <div class="step-icon-wrapper-4 secondary-icon">
                    <v-icon size="40" color="white">mdi-clipboard-text-outline</v-icon>
                  </div>
                  <h3 class="step-title">Continuous Assessment</h3>
                  <p class="step-description">
                    Monitor your <strong>Compliance Assessment</strong> by viewing the completion
                    percentage of evidence requirements, as well as the status of created claims and
                    sub-claims.
                  </p>
                  <v-btn
                    color="green"
                    class="step-btn mt-3"
                    @click="$router.push({ name: 'ConformityAssessment' })"
                  >
                    <v-icon left small>mdi-eye</v-icon>
                    View Compliance Assessments
                  </v-btn>
                </div>
              </v-col>
            </v-row>

            <v-divider class="my-5"></v-divider>

            <!-- Evidence Collection Section -->
            <div class="evidence-collection-section mb-6">
              <div class="evidence-header">
                <div class="evidence-content">
                  <div class="d-flex align-center mb-3">
                    <v-avatar size="56" class="mr-4 evidence-logo">
                      <v-icon size="32" color="white">mdi-cloud-upload-outline</v-icon>
                    </v-avatar>
                    <div>
                      <h2 class="evidence-title">Evidence Collection</h2>
                      <p class="evidence-subtitle">Upload and manage your compliance evidence</p>
                    </div>
                  </div>
                </div>
              </div>
              <v-card-text class="evidence-body pt-6 pb-6">
                <v-row align="center">
                  <v-col cols="12" md="8">
                    <p class="text-body-1 mb-3">
                      Build a strong compliance case by uploading
                      <strong>supporting documentation and evidence</strong>
                      that demonstrates your system's adherence to security standards and regulatory
                      requirements.
                    </p>
                    <ul class="evidence-list">
                      <li>
                        <strong>Security Documentation:</strong> Policies, procedures, and design
                        documents
                      </li>
                      <li>
                        <strong>Test Reports:</strong> Penetration testing, vulnerability
                        assessments, and audit logs
                      </li>
                      <li>
                        <strong>Implementation Evidence:</strong> Screenshots, configurations, and
                        deployment records
                      </li>
                      <li>
                        <strong>Compliance Records:</strong> Certifications, incident reports, and
                        maintenance logs
                      </li>
                    </ul>
                  </v-col>
                  <v-col cols="12" md="4" class="d-flex justify-center">
                    <v-btn
                      color="info"
                      size="large"
                      class="white--text evidence-btn"
                      @click="$router.push({ name: 'EvidenceList' })"
                    >
                      <v-icon left>mdi-file-document-multiple-outline</v-icon>
                      Go to Evidence Management
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </div>

            <!-- View Full Workflow Guide Button -->
            <div class="workflow-footer">
              <v-btn
                outlined
                color="primary"
                class="workflow-guide-btn"
                @click="$router.push({ name: 'Helper', query: { tab: 'workflow' } })"
              >
                <v-icon left>mdi-book-outline</v-icon>
                View Full Workflow Guide
              </v-btn>
            </div>
          </div>

          <v-divider class="my-5"></v-divider>

          <!-- Features & Tools Grid -->
          <v-row>
            <v-col cols="12" md="6">
              <div class="feature-box">
                <div class="feature-box-header">
                  <v-icon color="primary" class="mr-2">mdi-view-grid-outline</v-icon>
                  <span class="subtitle-1 font-weight-bold">Platform Features</span>
                </div>
                <v-divider class="my-2"></v-divider>
                <ul class="feature-list-modern">
                  <li>
                    <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>
                    Create Subjects of Certification for your systems
                  </li>
                  <li>
                    <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>
                    Build and manage Security Assurance Cases
                  </li>
                  <li>
                    <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>
                    Upload and organize evidence for compliance
                  </li>
                  <li>
                    <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>
                    Track requirements and manage standards adherence
                  </li>
                  <li>
                    <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>
                    Conduct conformity assessments with detailed reports
                  </li>
                </ul>
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <div class="feature-box tools-box">
                <div class="feature-box-header">
                  <v-icon color="deep-orange" class="mr-2">mdi-tools</v-icon>
                  <span class="subtitle-1 font-weight-bold">Integrated Tools</span>
                </div>
                <v-divider class="my-2"></v-divider>
                <ul class="feature-list-modern">
                  <li>
                    <v-icon small color="deep-orange" class="mr-2">mdi-spider-web</v-icon>
                    <strong>Threat Spider</strong> - Threat modeling and risk analysis
                  </li>
                  <li>
                    <v-icon small color="info" class="mr-2">mdi-sitemap</v-icon>
                    <strong>VimPact</strong> - Software component management
                  </li>
                  <li>
                    <v-icon small color="error" class="mr-2">mdi-bug-outline</v-icon>
                    <strong>Exclaim Scorer</strong> - JSON format Security Assurance Case validation
                  </li>
                  <li>
                    <v-icon small color="deep-purple" class="mr-2">mdi-creation</v-icon>
                    <strong>SAC Composer</strong> - Building security assurance cases with AI
                    assistance
                  </li>
                </ul>
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-5"></v-divider>

          <!-- Standards Support -->
          <div class="standards-section">
            <div class="section-header mb-3">
              <v-icon color="primary" size="28" class="mr-2">mdi-certificate-outline</v-icon>
              <span class="text-h6 font-weight-bold">Supported Standards & Regulations</span>
            </div>
            <div class="standards-chips">
              <v-chip class="ma-1 standard-chip" color="primary" outlined label>
                <v-icon left small>mdi-factory</v-icon>
                IEC 62443-4-1
              </v-chip>
              <v-chip class="ma-1 standard-chip" color="secondary" outlined label>
                <v-icon left small>mdi-chip</v-icon>
                IEC 62443-4-2
              </v-chip>
              <v-chip class="ma-1 standard-chip" color="info" outlined label>
                <v-icon left small>mdi-account-hard-hat</v-icon>
                IEC 62443-2-4
              </v-chip>
              <v-chip class="ma-1 standard-chip" color="green" outlined label>
                <v-icon left small>mdi-shield-star</v-icon>
                Cyber Resilience Act (CRA)
              </v-chip>
            </div>
          </div>

          <!-- Connect With Us -->
          <v-divider class="my-5"></v-divider>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Dashboard Metrics -->
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-card class="mb-4">
            <v-card-title class="dashboard-section-title">
              <v-icon color="white" class="mr-2">mdi-chart-donut</v-icon>
              Dashboard Metrics
            </v-card-title>
            <v-card-text>
              <p class="text-body-2 mb-4"></p>
              <v-row>
                <!-- SoCs Count Card -->
                <v-col cols="12" sm="6" lg="3">
                  <v-card class="metric-card primary-gradient" dark>
                    <v-card-text class="d-flex flex-column align-center py-6">
                      <v-icon size="48" class="mb-2">mdi-checkbox-marked-outline</v-icon>
                      <div class="text-h3 font-weight-bold">{{ totalSocs }}</div>
                      <div class="text-subtitle-1">Subjects of Certification</div>
                      <v-btn
                        v-if="totalSocs === 0"
                        color="white"
                        text
                        class="mt-2"
                        :to="{ name: 'SubjectOfCertification' }"
                      >
                        Create Subject of Certification
                        <v-icon right small>mdi-arrow-right</v-icon>
                      </v-btn>
                      <v-btn v-else color="white" text class="mt-2" :to="{ name: 'SoCList' }">
                        View All
                        <v-icon right small>mdi-arrow-right</v-icon>
                      </v-btn>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- SACs Count Card -->
                <v-col cols="12" sm="6" lg="3">
                  <v-card class="metric-card secondary-gradient" dark>
                    <v-card-text class="d-flex flex-column align-center py-6">
                      <v-icon size="48" class="mb-2">mdi mdi-security</v-icon>
                      <div class="text-h3 font-weight-bold">{{ totalSacs }}</div>
                      <div class="text-subtitle-1">Security Assurance Cases</div>
                      <v-btn
                        v-if="totalSacs === 0"
                        color="white"
                        text
                        class="mt-2"
                        :to="{ name: 'SacBuilder' }"
                      >
                        Create Security Assurance Case
                        <v-icon right small>mdi-arrow-right</v-icon>
                      </v-btn>
                      <v-btn
                        v-else
                        color="white"
                        text
                        class="mt-2"
                        :to="{ name: 'SecurityAssuranceCase' }"
                      >
                        View All
                        <v-icon right small>mdi-arrow-right</v-icon>
                      </v-btn>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Compliance Assessments Count Card -->
                <v-col cols="12" sm="6" lg="3">
                  <v-card class="metric-card accent-gradient" dark>
                    <v-card-text class="d-flex flex-column align-center py-6">
                      <v-icon size="48" class="mb-2">mdi-clipboard-text-outline</v-icon>
                      <div class="text-h3 font-weight-bold">{{ totalAssessments }}</div>
                      <div class="text-subtitle-1">Compliance Assessments</div>
                      <v-btn
                        v-if="totalAssessments === 0"
                        color="white"
                        text
                        class="mt-2"
                        :to="{ name: 'ConformityAssessment' }"
                      >
                        Create Assessment
                        <v-icon right small>mdi-arrow-right</v-icon>
                      </v-btn>
                      <v-btn
                        v-else
                        color="white"
                        text
                        class="mt-2"
                        :to="{ name: 'ConformityAssessment' }"
                      >
                        View All
                        <v-icon right small>mdi-arrow-right</v-icon>
                      </v-btn>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Approved Evidence Count Card -->
                <v-col cols="12" sm="6" lg="3">
                  <v-card class="metric-card success-gradient" dark>
                    <v-card-text class="d-flex flex-column align-center py-6">
                      <v-icon size="48" class="mb-2">mdi-check-decagram</v-icon>
                      <div class="text-h3 font-weight-bold">{{ approvedEvidenceCount }}</div>
                      <div class="text-subtitle-1">Approved Evidence</div>
                      <v-btn color="white" text class="mt-2" :to="{ name: 'EvidenceList' }">
                        View Evidence
                        <v-icon right small>mdi-arrow-right</v-icon>
                      </v-btn>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Latest Activity Section -->
      <v-row class="mt-6">
        <v-col cols="12">
          <v-card>
            <v-card-title class="dashboard-section-title">
              <v-icon color="white" class="mr-2">mdi-history</v-icon>
              Certification Progress Overview
            </v-card-title>
            <v-card-text>
              <p class="text-body-2 mb-4"></p>
              <v-row v-if="loaded && (totalSacs > 0 || totalSocs > 0)">
                <!-- SoC Progress -->
                <v-col cols="12" md="4">
                  <div class="progress-card">
                    <div class="d-flex justify-space-between align-center">
                      <div class="text-subtitle-1 font-weight-bold">
                        Subjects of Certification by Standard
                      </div>
                      <div class="text-caption">Total: {{ totalSocs }}</div>
                    </div>
                    <v-divider class="my-2"></v-divider>
                    <div v-if="standardFamilies.length" class="mt-4">
                      <v-chip
                        v-for="family in standardFamilies"
                        :key="family.name"
                        class="ma-1"
                        :color="getStandardColor(family.name)"
                        small
                        outlined
                      >
                        {{ family.name }}: {{ family.count }}
                      </v-chip>
                    </div>
                    <div v-else class="text-center grey--text py-4">
                      No standard families found
                    </div>
                  </div>
                </v-col>

                <!-- Compliance Assessment Scores - Updated -->
                <v-col cols="12" md="4">
                  <div class="progress-card">
                    <div class="d-flex justify-space-between align-center">
                      <div class="text-subtitle-1 font-weight-bold">
                        Compliance Assessment Scores
                      </div>
                      <div class="text-caption">
                        <strong
                          >{{ totalAssessments }} Assessment{{
                            totalAssessments !== 1 ? 's' : ''
                          }}</strong
                        >
                      </div>
                    </div>
                    <v-divider class="my-2"></v-divider>
                    <div v-if="totalAssessments > 0" class="mt-4">
                      <div v-if="complianceAssessments.length > 0" class="mt-2">
                        <div class="text-caption font-weight-bold mb-2">Recent Assessments:</div>
                        <div
                          v-for="assessment in complianceAssessments.slice(0, 3)"
                          :key="`${assessment.socId}-${assessment.sacId}`"
                          class="d-flex justify-space-between align-center mb-1"
                        >
                          <div class="text-caption text-truncate" style="max-width: 60%;">
                            {{ assessment.socName }}
                          </div>
                          <v-chip x-small :color="getScoreColor(assessment.score)" dark>
                            {{ assessment.score }}%
                          </v-chip>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-center grey--text py-4">
                      No compliance assessments yet
                    </div>
                  </div>
                </v-col>

                <!-- Evidence Status -->
                <v-col cols="12" md="4">
                  <div class="progress-card">
                    <div class="d-flex justify-space-between align-center">
                      <div class="text-subtitle-1 font-weight-bold">Evidence Status</div>
                      <div class="text-caption">Total: {{ totalEvidenceCount }}</div>
                    </div>
                    <v-divider class="my-2"></v-divider>
                    <div class="mt-4">
                      <v-row no-gutters>
                        <v-col cols="6">
                          <div class="text-center">
                            <v-icon color="success">mdi-check-circle</v-icon>
                            <div class="text-subtitle-1">{{ approvedEvidenceCount }}</div>
                            <div class="text-caption">Approved</div>
                          </div>
                        </v-col>
                        <v-col cols="6">
                          <div class="text-center">
                            <v-icon color="warning">mdi-clock-outline</v-icon>
                            <div class="text-subtitle-1">{{ pendingEvidenceCount }}</div>
                            <div class="text-caption">Pending</div>
                          </div>
                        </v-col>
                      </v-row>
                    </div>
                  </div>
                </v-col>
              </v-row>

              <!-- Empty state -->
              <div v-else-if="loaded" class="empty-state-large">
                <v-icon size="64" color="grey lighten-1">mdi-chart-timeline-variant</v-icon>
                <div class="text-h6 grey--text text--darken-1 mt-4">No certification data yet</div>
                <div class="text-body-2 grey--text mt-2 mb-6 text-center">
                  Start by creating a Subject of Certification (SoC) and build your compliance
                  journey from there.
                </div>
                <div class="d-flex justify-center">
                  <v-btn color="primary" class="mr-4" :to="{ name: 'SubjectOfCertification' }">
                    Create Subject of Certification
                  </v-btn>
                  <v-btn outlined color="primary" :to="{ name: 'ConformityAssessment' }">
                    View Assessments
                  </v-btn>
                </div>
              </div>

              <!-- Loading state -->
              <div v-else class="d-flex justify-center align-center" style="min-height: 200px;">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import securityAssuranceCaseApi from '@/modules/sac/api/securityAssuranceCaseApi';
import socApi from '@/modules/soc/api/soc';
import evidenceApi from '@/modules/evidence///api/evidenceApi';
import EventBus from '@/helpers/event-bus.js';

export default {
  name: 'DashboardContent',
  components: {},
  data() {
    return {
      loaded: false,
      loadingAssessments: true,
      loadingSocs: true,
      loadingSacs: true,
      loadingEvidence: true,

      // Data containers
      socList: [],
      sacList: [],
      sacScores: [],
      evidenceList: [],
      complianceAssessments: [], // Changed from assessmentScores
    };
  },
  computed: {
    // SoC metrics
    totalSocs() {
      return this.socList.length || 0;
    },
    standardFamilies() {
      if (!this.socList || !this.socList.length) return [];

      // Count by standard family
      const familyCounts = {};
      this.socList.forEach(soc => {
        const family = soc.standardFamily || 'Unknown';
        familyCounts[family] = (familyCounts[family] || 0) + 1;
      });

      // Convert to array for rendering
      return Object.entries(familyCounts).map(([name, count]) => ({
        name,
        count,
      }));
    },

    // SAC metrics
    totalSacs() {
      return this.sacList.length || 0;
    },
    avgSacScore() {
      if (!this.sacScores || !this.sacScores.length) return 0;
      const sum = this.sacScores.reduce((acc, curr) => acc + (Number(curr.score) || 0), 0);
      return Math.round(sum / this.sacScores.length);
    },

    // Assessment metrics - Updated
    totalAssessments() {
      return this.complianceAssessments.length || 0;
    },
    avgComplianceScore() {
      if (!this.complianceAssessments || !this.complianceAssessments.length) return 0;
      const sum = this.complianceAssessments.reduce(
        (acc, curr) => acc + (Number(curr.score) || 0),
        0,
      );
      return Math.round(sum / this.complianceAssessments.length);
    },

    // Evidence metrics
    totalEvidenceCount() {
      return this.evidenceList.length || 0;
    },
    approvedEvidenceCount() {
      return (
        this.evidenceList.filter(
          evidence => evidence.status && evidence.status.toLowerCase() === 'approved',
        ).length || 0
      );
    },
    pendingEvidenceCount() {
      return this.totalEvidenceCount - this.approvedEvidenceCount;
    },
  },
  async created() {
    // Set loaded to true by default to ensure UI isn't blocked indefinitely
    setTimeout(() => {
      if (!this.loaded) this.loaded = true;
    }, 3000);

    try {
      // Load data in parallel for better performance
      await Promise.all([
        this.fetchSocs(),
        this.fetchSacs(),
        this.fetchSacScores(),
        this.fetchComplianceAssessments(), // Changed method name
        this.fetchEvidence(),
      ]);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      EventBus.$emit('snackbar', {
        text: 'Some dashboard data could not be loaded',
        color: 'warning',
      });
    } finally {
      this.loaded = true;
    }
  },
  methods: {
    async fetchSocs() {
      this.loadingSocs = true;
      try {
        const response = await socApi.all();
        this.socList = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error('Error fetching Subjects of Certification:', error);
        this.socList = [];
      } finally {
        this.loadingSocs = false;
      }
    },
    async fetchSacs() {
      this.loadingSacs = true;
      try {
        const response = await securityAssuranceCaseApi.all();
        this.sacList = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error('Error fetching Security Assurance Cases:', error);
        this.sacList = [];
      } finally {
        this.loadingSacs = false;
      }
    },
    async fetchSacScores() {
      try {
        const response = await securityAssuranceCaseApi.getScores();
        this.sacScores = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error('Error fetching SAC scores:', error);
        this.sacScores = [];
      }
    },
    async fetchComplianceAssessments() {
      this.loadingAssessments = true;
      try {
        // Fetch all data needed for compliance assessments
        const [socResponse, sacResponse, scoresResponse] = await Promise.all([
          socApi.all(),
          securityAssuranceCaseApi.all(),
          securityAssuranceCaseApi.getScores(),
        ]);

        const socs = Array.isArray(socResponse.data) ? socResponse.data : [];
        const sacs = Array.isArray(sacResponse.data) ? sacResponse.data : [];
        const scores = Array.isArray(scoresResponse.data) ? scoresResponse.data : [];

        // Build compliance assessments by matching SoC -> SAC -> Score
        this.complianceAssessments = socs
          .map(soc => {
            // Find SAC(s) associated with this SoC's current version
            const associatedSacs = sacs.filter(sac => sac.socVersionId === soc.currentVersionId);

            // For each associated SAC, get its score and create an assessment entry
            return associatedSacs.map(sac => {
              const score = scores.find(s => s.id === sac.id);
              return {
                socId: soc.id,
                socName: soc.name,
                sacId: sac.id,
                sacName: sac.name,
                standardFamily: soc.standardFamily,
                score: score ? Number(score.score) : 0,
              };
            });
          })
          .flat()
          .filter(assessment => assessment !== null);
      } catch (error) {
        console.error('Error fetching Compliance Assessments:', error);
        this.complianceAssessments = [];
      } finally {
        this.loadingAssessments = false;
      }
    },
    async fetchEvidence() {
      this.loadingEvidence = true;
      try {
        const response = await evidenceApi.all();
        this.evidenceList = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error('Error fetching Evidence:', error);
        this.evidenceList = [];
      } finally {
        this.loadingEvidence = false;
      }
    },
    getScoreColor(score) {
      if (score >= 80) return 'success';
      if (score >= 60) return 'warning';
      return 'error';
    },
    getStandardColor(standardFamily) {
      const colorMap = {
        'IEC 62443-4-1': 'primary',
        'IEC 62443-4-2': 'secondary',
        'IEC 62443-2-4': 'info',
        'IEC 62443-3-3': 'purple',
        CRA: 'green',
        Unknown: 'grey',
      };

      return colorMap[standardFamily] || 'blue';
    },
  },
};
</script>

<style>
.v-application .justify-center2 {
  justify-content: center !important;
  margin-bottom: 150px;
}

/* Guide Card Styling */
.guide-card {
  border-left: 4px solid #1976d2;
  margin-bottom: 20px;
  background-color: #f9f9f9;
  border-color: #e0e0e0;
}

/* Status Box Styling */
.status-box {
  padding: 16px;
  border-radius: 8px;
  height: 100%;
  transition: transform 0.2s;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
}

.status-box:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.status-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.feature-list {
  padding-left: 16px;
  margin-top: 8px;
}

.feature-list li {
  position: relative;
  padding-left: 24px;
  margin-bottom: 8px;
  line-height: 1.5;
}

.feature-list li:before {
  content: '✓';
  position: absolute;
  left: 0;
  top: 0;
  color: var(--v-primary-base);
  font-weight: bold;
  margin-right: 8px;
}

/* Dashboard Metrics Styling */
.dashboard-section-title {
  background: linear-gradient(135deg, #00405e 0%, #006494 50%, #0097a7 100%);
  color: white;
  font-weight: 600;
  border-radius: 4px 4px 0 0;
}

.metric-card {
  height: 100%;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  transition: transform 0.3s, box-shadow 0.3s;
  overflow: hidden;
}

.metric-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15) !important;
}

.primary-gradient {
  background: linear-gradient(135deg, #1976d2 0%, #64b5f6 100%);
}

.secondary-gradient {
  background: linear-gradient(135deg, #7b1fa2 0%, #ba68c8 100%);
}

.accent-gradient {
  background: linear-gradient(135deg, #00405e 0%, #0097a7 100%);
}

.success-gradient {
  background: linear-gradient(135deg, #388e3c 0%, #81c784 100%);
}

.progress-card {
  background-color: #fff;
  border-radius: 8px;
  padding: 16px;
  height: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s;
  border: 1px solid #e0e0e0;
}

.progress-card:hover {
  transform: translateY(-3px);
  /*box-shadow: 0 8px 24px rgba(5, 81, 247, 0.301); */
  border-color: #1976d2;
}

.empty-state-large {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  padding: 32px;
  text-align: center;
}

/* Button styling */
.button-container {
  margin-top: 16px;
  margin-bottom: 8px;
}

.button-container .v-btn {
  text-transform: none;
  font-weight: 500;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.button-container .v-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
}

/* ============================================
   HERO GUIDE CARD - Modern Redesign Styles
   ============================================ */

.hero-guide-card {
  border-radius: 16px !important;
  overflow: hidden;
  border: none !important;
  background: #ffffff;
}

.hero-header {
  background: linear-gradient(135deg, #00405e 0%, #006494 50%, #0097a7 100%);
  padding: 24px 28px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.hero-header-content {
  flex: 1;
}

.hero-logo {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(4px);
}

.hero-title {
  color: white;
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
}

.hero-subtitle {
  color: rgba(255, 255, 255, 0.85);
  font-size: 1rem;
  margin: 0;
  font-weight: 400;
}

.social-links {
  display: flex;
  gap: 8px;
}

.social-btn {
  background: rgba(1, 20, 71, 0.411) !important;
  color: white !important;
  transition: all 0.3s ease;
}

.social-btn:hover {
  background: rgba(255, 255, 255, 0.3) !important;
  transform: translateY(-2px);
}

.intro-text {
  color: #424242;
  line-height: 1.7;
  font-size: 1.05rem;
}

/* Workflow Section */
.workflow-section {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 16px;
  padding: 28px;
  margin-top: 8px;
}

.section-header {
  display: flex;
  align-items: center;
}

.workflow-steps {
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: center;
}

.workflow-step {
  background: white;
  border-radius: 16px;
  padding: 24px 20px;
  text-align: center;
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.workflow-step:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
  background: linear-gradient(135deg, #ffffff 0%, #f0f4ff 100%);
}

.workflow-step.step-1:hover {
  background: linear-gradient(135deg, #ffffff 0%, #fff3e0 100%);
}

.workflow-step.step-3:hover {
  background: linear-gradient(135deg, #ffffff 0%, #f3e5f5 100%);
}

.workflow-step.step-4:hover {
  background: linear-gradient(135deg, #ffffff 0%, #dcf7d2 100%);
}

.step-number {
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.4);
}

.step-number-3 {
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #da42f1, #d761e9);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.4);
}

.step-number-4 {
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #68e939, #7ce456);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.4);
}

.step-number.recommended {
  background: linear-gradient(135deg, #ff5722, #ff9800);
  box-shadow: 0 4px 12px rgba(255, 87, 34, 0.4);
}

.step-icon-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 12px 0 16px 0;
  box-shadow: 0 8px 24px rgba(25, 118, 210, 0.3);
  transition: all 0.3s ease;
}

.step-icon-wrapper-4 {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: linear-gradient(135deg, #28d219, #62eb12);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 12px 0 16px 0;
  box-shadow: 0 8px 24px rgba(25, 118, 210, 0.3);
  transition: all 0.3s ease;
}

.step-icon-wrapper.recommended-icon {
  background: linear-gradient(135deg, #ff5722, #ff9800);
  box-shadow: 0 8px 24px rgba(255, 87, 34, 0.3);
}

.step-icon-wrapper.secondary-icon {
  background: linear-gradient(135deg, #7b1fa2, #ba68c8);
  box-shadow: 0 8px 24px rgba(123, 31, 162, 0.3);
}

.workflow-step:hover .step-icon-wrapper {
  transform: scale(1.05);
}

.recommended-badge {
  position: absolute;
  top: 4px;
  right: 5px;
  background: linear-gradient(135deg, #ff5722, #e64a19);
  animation: pulse 3s ease-in infinite;
  color: white;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0 2px 8px rgba(255, 87, 34, 0.35);
}

.step-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1a237e;
  margin-bottom: 12px;
  line-height: 1.3;
}

.step-description {
  color: #616161;
  font-size: 0.875rem;
  line-height: 1.6;
  flex-grow: 1;
  margin-bottom: 8px;
}

.step-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  padding: 0 20px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  transition: all 0.3s ease !important;
}

.step-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2) !important;
}

/* Arrow connectors between workflow steps */
.workflow-col {
  position: relative;
}

.workflow-col:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 50%;
  right: -12px;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 12px solid transparent;
  border-bottom: 12px solid transparent;
  border-left: 14px solid #1976d2;
  z-index: 10;
  filter: drop-shadow(2px 0 4px rgba(25, 118, 210, 0.3));
}

@media (max-width: 960px) {
  .workflow-col:not(:last-child)::after {
    display: none;
  }
}

/* Workflow Footer */
.workflow-footer {
  display: flex;
  justify-content: center;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid rgba(25, 118, 210, 0.1);
}

.workflow-guide-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  padding: 10px 24px !important;
  transition: all 0.3s ease !important;
  box-shadow: 0 4px 20px rgba(53, 25, 210, 0.2),
    /* subtle shadow */ 0 0 30px rgba(105, 25, 210, 0.6),
    /* glow */ 0 0 60px rgba(152, 25, 210, 0.4); /* stronger outer glow */
}

.workflow-guide-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 24px rgba(53, 25, 210, 0.2),
    /* subtle shadow */ 0 0 32px rgba(105, 25, 210, 0.6),
    /* glow */ 0 0 64px rgba(152, 25, 210, 0.4); /* stronger outer glow */
}

/* Evidence Collection Section */
.evidence-collection-section {
  background: linear-gradient(135deg, #f0f7ff 0%, #e8f4ff 100%);
  border-radius: 16px;
  padding: 0;
  overflow: hidden;
  border: 2px solid #00acc1;
  box-shadow: 0 4px 16px rgba(0, 172, 193, 0.15);
}

.evidence-header {
  background: linear-gradient(135deg, #00acc1 0%, #00838f 100%);
  padding: 8px 28px;
  display: flex;
  align-items: center;
}

.evidence-content {
  flex: 1;
}

.evidence-logo {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(4px);
}

.evidence-title {
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
}

.evidence-subtitle {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.95rem;
  margin: 0;
  font-weight: 400;
}

.evidence-body {
  background: white;
}

.evidence-list {
  list-style: none;
  padding: 0;
  margin: 16px 0 0 0;
}

.evidence-list li {
  display: flex;
  align-items: flex-start;
  padding: 8px 0;
  padding-left: 24px;
  position: relative;
  color: #424242;
  font-size: 0.95rem;
  line-height: 1.6;
}

.evidence-list li:before {
  content: '✓';
  position: absolute;
  left: 0;
  color: #00acc1;
  font-weight: bold;
  font-size: 1.1rem;
}

.evidence-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  padding: 12px 28px !important;
  box-shadow: 0 4px 12px rgba(0, 172, 193, 0.3) !important;
  transition: all 0.3s ease !important;
}

.evidence-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 172, 193, 0.4) !important;
}

/* Feature Boxes */
.feature-box {
  background: #ffffff;
  border-radius: 12px;
  padding: 20px;
  height: 100%;
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.feature-box:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  border-color: #1976d2;
}

.feature-box.tools-box:hover {
  border-color: #ff5722;
}

.feature-box-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.feature-list-modern {
  list-style: none;
  padding: 0;
  margin: 12px 0 0 0;
}

.feature-list-modern li {
  display: flex;
  align-items: flex-start;
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f5;
  color: #424242;
  font-size: 0.9rem;
  line-height: 1.5;
}

.feature-list-modern li:last-child {
  border-bottom: none;
}

.feature-list-modern li .v-icon {
  margin-top: 2px;
  flex-shrink: 0;
}

/* Standards Section */
.standards-section {
  text-align: center;
}

.standards-chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
}

.standard-chip {
  font-weight: 500 !important;
  transition: all 0.3s ease !important;
}

.standard-chip:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Connect/Social Section */
.connect-section {
  text-align: center;
}

.social-cards {
  margin-top: 8px;
}

.social-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 16px;
  background: #f8f9fa;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  height: 100%;
  min-height: 100px;
}

.social-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.social-card.website-card:hover {
  background: #e3f2fd;
  border-color: #1976d2;
}

.social-card.twitter-card:hover {
  background: #e8f5fe;
  border-color: #1da1f2;
}

.social-card.youtube-card:hover {
  background: #ffebee;
  border-color: #ff0000;
}

.social-card.linkedin-card:hover {
  background: #e8f4fc;
  border-color: #0a66c2;
}

.social-card-label {
  margin-top: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #424242;
}

/* Responsive Adjustments */
@media (max-width: 960px) {
  .hero-header {
    flex-direction: column;
    text-align: center;
  }

  .hero-header-content {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .social-links {
    justify-content: center;
  }

  .workflow-step {
    margin-bottom: 24px;
  }
}

@media (max-width: 600px) {
  .hero-title {
    font-size: 1.4rem;
  }

  .workflow-section {
    padding: 20px 16px;
  }

  .step-icon-wrapper {
    width: 64px;
    height: 64px;
  }

  .step-icon-wrapper .v-icon {
    font-size: 32px !important;
  }
}

/* Helper Card Styles */
.helper-card {
  background: linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%);
  border-left: 5px solid #1976d2;
}

.helper-card .text-primary-600 {
  color: #1976d2;
}

.helper-card .text-secondary {
  color: #757575;
}
</style>
